import {
    MDBListGroup,
    MDBListGroupItem,
    MDBBadge,
} from "mdb-react-ui-kit"
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useDeleteAnswersAdjustmentMutation } from "./Answer.state";
import { ConfirmationModal } from "../common";
import { FontAwesomeIcon } from "../common/SideNav.styles";
import { faComment, faBookOpen, faEraser, faTrash } from "@fortawesome/free-solid-svg-icons";
import HighlightList from "../common/HighlightList";
import { Button } from "../App.styles";
import useGameRole from "../hooks/useGameRole";
import { useState } from "react";
import AnswerAdjustmentsEdit from "./AnswerAdjustmentsEdit";
import flameIceCold from "../images/flame-ice-cold.json"
import flameCold from "../images/flame-cold.json"
import flameWarm from "../images/flame-warm.json"
import flameHot from "../images/flame-hot.json"
import flameVeryHot from "../images/flame-very-hot.json"
import Lottie from "react-lottie";

const getGradientForScore = (score) => {
    if (score <= 20) return flameIceCold;  // Ice cold (light blue to cyan)
    if (score <= 40) return flameCold;    // Cold (cyan to sky blue)
    if (score <= 60) return flameWarm;  // Warm (sky blue to green)
    if (score <= 80) return flameHot;   // Hot (green to orange)
    return flameVeryHot;                       // Very hot (orange to red)
};

const GreatnessScore = ({ score, children }) => {
    const gradient = getGradientForScore(score);
    const animationOptions = {
        loop: true,
        autoplay: true,
        animationData: gradient,
        rendererSettings: {
          preserveAspectRatio: "xMidYMid",
        },
    };

    return (
        <div className="d-flex align-items-center animation--flame">
            <span>{children}</span> <Lottie options={animationOptions} height={24} width={24} />
        </div>
    );
};

const AdjustmentList = styled(MDBListGroup)(({ theme }) => `
    & + & {
        margin-top: 12px;
    }

    &.disabled {
        .list-group-item:not(:last-of-type) span,
        .list-group-item:not(:last-of-type) blockquote,
        .list-group-item:not(:last-of-type) small {
            opacity: 0.5;
        }

        .list-group-item:last-of-type span {
            text-decoration: line-through;
        }
    }

    .svg-inline--fa {
        color: ${theme.disabledColor};
    }

    .bonus {
        color: ${theme.successColor};
    }

    .penalty {
        color: ${theme.dangerColor};
    }

    .animation--flame svg {
        display: block;
        transform: translate3d(0, -4px, 0) !important;
    }

    .adjustment-list__item {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 6px;
    }

    blockquote {
        font-style: italic;           /* Makes the text italic */
        font-size: 1em;              /* Increases the size of the quote */
        quotes: "“" "”" "‘" "’";      /* Defines custom quotes (double and single) */
        margin-bottom: 0;
    }

    blockquote::before {
        content: open-quote;          /* Adds opening quote before the blockquote */
        font-size: 1em;              /* Increases the size of the quote */
        color: #ccc;                  /* Lighter color for the quote mark */
    }

    blockquote::after {
        content: close-quote;         /* Adds closing quote after the blockquote */
        font-size: 1rem;            /* Increases the size of the quote */
        color: #ccc;                  /* Lighter color for the quote mark */
    }

    .adjustment-list__item__action {
        justify-content: start;
        flex-grow: 0;

        ${theme.breakpoints.up('lg')} {
            justify-content: end;
            flex-grow: 1;
        }
    }

    .icon--action {
        cursor: pointer;
    }
`)

const AdjustmentItem = styled(MDBListGroupItem)`
    display: flex;
    align-items: start;
    gap: 6px;

    .badge {
        margin-top: 2px;
    }
`

const AdjustmentItemHeader = styled.div(({ theme }) => `
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .btn {
        margin-bottom: 0;
    }
`)

const AnswerAdjustmentsView = (props) => {
    const {
        answerId,
        id: adjustmentId, comment, summary, decremental,
        points, correct, greatness, impacts_leaderboard,
        isStreamPage,
    } = props
    const { t } = useTranslation()
    const { mutateAsync: deleteAdjustment, isLoading: isDeletingAdjustment } = useDeleteAnswersAdjustmentMutation({ answerId })
    const { administrator, coordinator, super_admin } = useGameRole()
    const [edit, setEdit] = useState(false)

    return edit
        ? <AnswerAdjustmentsEdit {...props} onCancel={() => setEdit(false)} />
        : (
            <HighlightList id={adjustmentId}>
                <AdjustmentList id={adjustmentId} className={impacts_leaderboard ? '' : 'disabled'}>
                    <AdjustmentItem>
                        <AdjustmentItemHeader>
                            <div className="adjustment-list__item">
                                <MDBBadge color={correct ? `success` : `danger`} light>
                                    {correct ? t(`Correct!`) : t(`Incorrect!`)}
                                </MDBBadge>
                                <GreatnessScore score={greatness}>{greatness} {t(`greatness boost.`)}</GreatnessScore>
                            </div>
                            {
                                isStreamPage
                                    ? null
                                    : (
                                        <>
                                            <div className="adjustment-list__item adjustment-list__item__action">
                                                {
                                                    (administrator || coordinator || super_admin) && impacts_leaderboard
                                                        ? (
                                                            <Button size="sm" className="btn--flat btn--flat-sm d-block" onClick={() => setEdit(true)}>
                                                                {t('Edit')}
                                                            </Button>
                                                        )
                                                        : null
                                                }
                                            </div>
                                        </>
                                    )
                            }
                        </AdjustmentItemHeader>
                    </AdjustmentItem>
                    <AdjustmentItem>
                        <FontAwesomeIcon icon={faComment} />
                        <blockquote>{comment}</blockquote>
                    </AdjustmentItem>
                    {
                        isStreamPage
                            ? null
                            : (
                                <AdjustmentItem>
                                    <FontAwesomeIcon icon={faBookOpen} />
                                    <small>{summary}</small>
                                </AdjustmentItem>
                            )
                    }
                    <AdjustmentItem>
                        <div className="adjustment-list__item">
                            <FontAwesomeIcon icon={faEraser} />
                            <span className={decremental ? `penalty` : `bonus`}>
                                {decremental ? `-` : `+`}{points} {decremental ? t(`penalty`) : t(`bonus`)} {t(`points`)}.
                            </span>
                        </div>
                        <div className="adjustment-list__item adjustment-list__item__action">
                            {
                                isStreamPage
                                    ? null
                                    : (
                                        <ConfirmationModal
                                            title={t("Delete adjustment?")}
                                            body={t("Are you sure you would like to delete the adjustment?")}
                                            confirmLabel={t("Yes, delete")}
                                            confirmAction={() => deleteAdjustment(adjustmentId)}
                                            isLoading={isDeletingAdjustment}
                                        >
                                            <FontAwesomeIcon className="icon--action" icon={faTrash} />
                                        </ConfirmationModal>
                                    )
                            }
                        </div>
                    </AdjustmentItem>
                </AdjustmentList>
            </HighlightList>
        )
}

export default AnswerAdjustmentsView